import { type IntlShape } from "react-intl";
import { z } from "zod";

import { machineStatusSchema } from "../machine-status";
import { machineTypeSchema } from "../machine-type";
import { productionStatusSchema } from "../machine-production-status";
import { getMachineRequestBaseSchema, type MachineRequestBase } from "../machine-request-base";

const fusionLongHeadSchema = z
	.object({
		number: z.number(),
		type: z.enum(["cut", "crease", "tiling"]).optional(),
		leftSideToTool: z.number().optional(),
		rightSideToTool: z.number().optional(),
		position: z.number().optional(),
		minimumPosition: z.number().optional(),
		maximumPosition: z.number().optional(),
		width: z.number().optional(),
		toolToSensorOffset: z.number().optional(),
		leftSideToWasteSeparator: z.number().optional(),
		wasteSeparatorWidth: z.number().optional(),
		leftSideToSensorPin: z.number().optional(),
	})
	.passthrough();

export const fusionPhysicalMachineSettingsSchema = z
	.object({
		unit: z.enum(["mm", "inch"]).optional(),
		onTheFlyActive: z.boolean().optional(),
		enableDesignTrim: z.boolean().optional(),
		enableSideTrim: z.boolean().optional(),
		longHeadParameters: z
			.object({
				longHeadYOffset: z.number().optional(),
				wasteSeparatorWidth: z.number().optional(),
				wasteSeparatorActivationOffset: z.number().optional(),
				wasteSeparatorDeactivationOffset: z.number().optional(),
				connectionDelay: z.number().optional(),
				leftSideToSensorPin: z.number().optional(),
				maximumPosition: z.number().optional(),
				minimumPosition: z.number().optional(),
				positioningAcceleration: z.number().optional(),
				positioningSpeed: z.number().optional(),
				longHeadWidth: z.number().optional(),
				longHeads: z.array(fusionLongHeadSchema).optional(),
			})
			.passthrough(),
		feedRoller: z
			.object({
				acceleration: z.number().optional(),
				cleanCutLength: z.number().optional(),
				loweringOffset: z.number().optional(),
				outFeedLength: z.number().optional(),
				speed: z.number().optional(),
				toolsGearRatio: z.number().optional(),
			})
			.optional(),
		crossHead: z.object({
			acceleration: z.number().optional(),
			creaseCompensation: z.number().optional(),
			creaseWheelOffset: z.number().optional(),
			cutCompensation: z.number().optional(),
			homingPosition: z.number().optional(),
			knifeActivationDelay: z.number().optional(),
			knifeDeactivationDelay: z.number().optional(),
			leftSideToTool: z.number().optional(),
			longHeadSensorToTool: z.number().optional(),
			maximumAcceleration: z.number().optional(),
			maximumDeceleration: z.number().optional(),
			maximumPosition: z.number(),
			maximumSpeed: z.number().optional(),
			minimumLineLength: z.number().optional(),
			minimumPosition: z.number().optional(),
			position: z.number().optional(),
			scaling: z.number().optional(),
			speed: z.number().optional(),
			toolPositionToSensor: z.number().optional(),
			trackSensorToKnife: z.number().optional(),
			trackSensorToTool: z.number().optional(),
			width: z.number().optional(),
		}),
		trackParameters: z
			.object({
				activationDelay: z.number().optional(),
				sideSteeringTolerance: z.number().optional(),
				offsetTolerance: z.number().optional(),
				latchMinimumDistance: z.number().optional(),
				corrugateWidthTolerance: z.number().optional(),
			})
			.optional(),
		codeGenerator: z
			.object({
				minimumLineDistanceToCorrugateEdge: z.number().optional(),
				minimumLineLength: z.number().optional(),
			})
			.optional(),
	})
	.passthrough();

const fusionTrackSchema = z
	.object({
		number: z.number().optional(),
		trackNumber: z.number().optional(),
		outOfCorrugatePosition: z.number().optional(),
		leftSensorPlateToCorrugate: z.number().optional(),
		rightSensorPlateToCorrugate: z.number().optional(),
		trackOffset: z.number().optional(),
		leftSwordSensorCompensations: z.array(z.number()).optional(),
		rightSwordSensorCompensations: z.array(z.number()).optional(),
	})
	.passthrough();

export const fusionMachineConfigurationSchema = z.object({
	physicalMachineSettings: fusionPhysicalMachineSettingsSchema,
	tracks: z.array(fusionTrackSchema).min(1).max(2),
});

export const getFusionMachineRequestSchema = (intl: IntlShape, existingMachines: MachineRequestBase[] = []) =>
	getMachineRequestBaseSchema(intl, existingMachines)
		.extend({
			status: machineStatusSchema.optional(),
			productionStatus: productionStatusSchema.optional(),
			machineType: z.literal(machineTypeSchema.enum.Fusion),
			waitForReleaseTrigger: z.boolean().optional(),
			physicalMachineSettings: fusionPhysicalMachineSettingsSchema.nullish(),
			tracks: z.array(fusionTrackSchema).min(1).max(2),
		})
		.superRefine((request, context) => {
			const tracks = request.tracks;
			const unit = request.physicalMachineSettings?.unit ?? "mm";
			const isInches = unit === "inch";
			const trackRanges =
				tracks.length === 1
					? [{ minimum: isInches ? 50 : 1270, maximum: isInches ? 53 : 1350 }]
					: [
							{ minimum: 0, maximum: isInches ? 4 : 105 },
							{ minimum: isInches ? 50 : 1270, maximum: isInches ? 53 : 1350 },
						];

			tracks.forEach((track, index) => {
				const range = trackRanges[index];
				if (
					track.outOfCorrugatePosition === undefined ||
					track.outOfCorrugatePosition < range.minimum ||
					track.outOfCorrugatePosition > range.maximum
				) {
					context.addIssue({
						code: "custom",
						path: ["tracks", index, "outOfCorrugatePosition"],
						message: intl.formatMessage(
							{
								id: "Track {trackNumber} Out of Corrugate Position must be between {minimum} and {maximum}",
								defaultMessage: "Track {trackNumber} Out of z-Fold Position must be between {minimum} and {maximum}",
							},
							{ trackNumber: index + 1, minimum: range.minimum, maximum: range.maximum },
						),
					});
				}
				if (track.number === undefined) {
					track.number = track.trackNumber;
				}
			});
		});

export type FusionMachineRequest = z.infer<ReturnType<typeof getFusionMachineRequestSchema>>;
export type FusionMachineConfiguration = z.infer<typeof fusionMachineConfigurationSchema>;
export type FusionPhysicalMachineSettings = z.infer<typeof fusionPhysicalMachineSettingsSchema>;
